import { clsx } from 'clsx'
import { CircleButton } from 'component/Button/CircleButton'
import { Gap, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useBodyScrollLock } from 'hook/useBodyScrollLock'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { Buttons } from 'page/catalog/ItemPreview/Buttons'
import { ImagesPane } from 'page/catalog/ItemPreview/ImagesPane'
import { ItemDetails } from 'page/catalog/ItemPreview/ItemDetails'
import { ItemHeaderMobile } from 'page/catalog/ItemPreview/ItemHeaderMobile'
import { ItemTitle } from 'page/catalog/ItemPreview/ItemTitle'
import { SingleImage } from 'page/catalog/ItemPreview/SingleImage'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { itemPreview } from 'page/catalog/store/ItemPreviewStore'
import { useRef, useState } from 'react'
import { getShortcut } from 'util/keyboard'
import css from './ItemPreviewMobile.module.scss'

export const ItemPreviewMobile = observer(() => {
  const demo = useDemo()
  const { basic, stand } = demo
  const { descriptions } = demo.product.details ?? {}
  const { item, len } = itemPreview.it
  const { description } = item
  const ref = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(true)
  const [bottom, setBottom] = useState(true)
  const className = clsx(css.content, basic ? css.basic : stand ? css.stand : null)
  const round = stand ? 24 : 12
  const pad = stand ? 32 : 16
  const gap = stand ? 32 : 20
  const font = stand ? 20 : 13

  useBodyScrollLock(true)

  const onClose = useLatestCallback(() => {
    itemPreview.close()
  })

  useEffectOnce(() => {
    const listener = (event: KeyboardEvent) => {
      const shortcut = getShortcut(event)
      if (shortcut === 'Escape') onClose()
    }

    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  })

  useEffectOnce(() => {
    const scroll = ref.current
    if (!scroll) return

    const listener = () => {
      const end = scroll.scrollHeight - scroll.clientHeight - 1
      const top = Math.floor(scroll.scrollTop)
      setTop(top <= 0)
      setBottom(top >= end)
    }

    listener()
    window.addEventListener('resize', listener)
    scroll.addEventListener('scroll', listener)

    return () => {
      window.removeEventListener('resize', listener)
      scroll.removeEventListener('scroll', listener)
    }
  })

  return (<div className={css.root}>
    <div className={css.blur} />
    <Vertical className={className} round={round}>
      <Vertical ref={ref} className={css.scroll}>
        <Vertical flex="0 0 auto" height="50%" oh>
          {!len ? null : len > 1 ? <ImagesPane /> : <SingleImage />}
        </Vertical>
        <Vertical flex="1 0 auto" oh>
          <Gap height={pad} />
          <Vertical pad={[0, pad]} tall>
            <ItemTitle />
            <Gap height={gap} />
            <ItemDetails />
            <Gap height={gap} />
            {descriptions && description && <Vertical gap={4} spring>
              <Typo size={font} upper secondary>
                {i18n('demo.Description')}
              </Typo>
              <Typo size={font} pre wrap>
                {description}
              </Typo>
            </Vertical>}
          </Vertical>
        </Vertical>
        <Space className={clsx(css.bottom, !bottom && css.shadow)} pad={pad} round={round}>
          <Buttons />
        </Space>
      </Vertical>

      <Space pin={[8, 8, null, null]}>
        <CircleButton size={stand ? 64 : 32} onClick={onClose}>
          <Icon size={stand ? 32 : 20} name="close" />
        </CircleButton>
      </Space>

      {!top && <Space className={clsx(css.top, css.shadow)} round={round}>
        <ItemHeaderMobile />
      </Space>}
    </Vertical>
  </div>)
})
